import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  applyDocumentLocale,
  applyThemeAtBoot,
  browserSystemSchemeDetector,
} from '@agnivo/app-core';
import { registerMockApiModule } from '@agnivo/app-core';
import { App } from './App';
import './styles/index.css';

// Apply the persisted theme BEFORE first paint — the ThemeProvider in App.tsx
// re-applies and keeps it in sync, but running only there means the first frame
// paints with the default and then flips. mini-app does the same in its own
// main.tsx with the Telegram detector.
applyThemeAtBoot(browserSystemSchemeDetector);

// Declare the language we are ACTUALLY rendering, before React mounts.
//
// `detectLocale()` resolves `en` for an English user and the UI renders English, but
// `index.html` ships `lang="ru"` — so without this line an EN user is read English in
// Russian phonetics, because `lang` is what selects a screen reader's voice and
// pronunciation rules. Product decision 2026-08-30
// (`decisions/product/2026-08-30-document-lang-mirrors-ui-locale.md`): document `lang`
// mirrors the effective rendered locale; RU stays the fallback when no user-language
// signal exists.
//
// ⛔ SYNCHRONOUS, and before `createRoot`, on purpose. The static `lang="ru"` is kept as a
// parse-time fallback (product: acceptable if the bootstrap replaces it before English
// meaningfully renders) — and it does, because nothing in this surface's `index.html`
// renders text before the module script runs. The invariant product stated is that the
// rendered locale and `lang` never disagree; anything deferred here would open a window
// where they do.
applyDocumentLocale();


const container = document.getElementById('root');
if (!container) throw new Error('missing #root');

// T1 mock fold (web!515 unblock; Initiative 017): the mock api module is imported
// DYNAMICALLY, in this shell's source, exactly where Vite replaces the env read.
// In a live build the condition folds to `false`, the branch dies, and the mock
// package is not in the bundle at all — which is the point: the size gate measures
// every chunk in dist-shipped, so a lazily-loaded mock would still cost its bytes.
// In mock builds (dev/audit) the import resolves before React mounts, so the
// providers' mock arms compose synchronously exactly as before. One microtask of
// boot delay in live builds; zero difference after mount.
// T1 mock fold (web!515 unblock; Initiative 017): default VITE_API_MODE here
// MUST match src/config.ts's `(import.meta.env.VITE_API_MODE ?? 'mock')` — if
// they disagree, a build with the env var unset gets `config.apiMode === 'mock'`
// while the shell skips the registration, and ApiProvider's mock arm throws
// "never registered" on first render (the desktop e2e regression class — see
// `web!520` pipeline 2808614277, 2026-09-01).
//
// ⛔ **`@agnivo/api-client/mock/core`, NOT the `/mock` barrel** — mock-modules seam
// (2026-09-27, option C). `/`'s own screens never call a per-surface route (e.g. widget), so
// pulling the full barrel here would ship those route handlers' bytes in EVERY page's eager
// boot, credited nowhere. The admin-tenant section's own mock registration (which DOES need
// widget) moved to `routes/admin-tenant/entry.tsx`'s `ensureAdminMockRegistered`, structurally
// gated on that section's own lazy chunk — see its docblock. This file no longer registers the
// admin arm at all.
const mockApiBoot =
  (import.meta.env.VITE_API_MODE ?? 'mock') === 'mock'
    ? import('@agnivo/api-client/mock/core').then((m) => {
        registerMockApiModule(m);
      })
    : Promise.resolve();

void mockApiBoot.then(() => {
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
